import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAuth } from '../hooks/useAuth';
import { passwordCreationSchema, PasswordCreationFormData } from '../schemas';
import { faUser, faLock, faEye } from '@fortawesome/free-solid-svg-icons';
import FormInput from '../components/FormInput';
import Button from '../components/Button';
import Alert from '../components/Alert';

const PasswordPage: React.FC = () => {
  const { createPassword, error: errorAuth, user, currentOrganization } = useAuth();

  const email = user?.email;
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errorType, setErrorType] = useState<string>('');

  const {
    control,
    handleSubmit,
    formState: { isValid }
  } = useForm<PasswordCreationFormData>({
    resolver: zodResolver(passwordCreationSchema),
    mode: 'onChange',
    defaultValues: {
      name: '',
      password: '',
      passwordConfirm: ''
    }
  });

  const error = errorAuth || errorType;

  const onSubmit = async (data: PasswordCreationFormData): Promise<void> => {
    setIsLoading(true);
    setErrorType('');

    try {
      const success = await createPassword(data.name, data.password);
      if (success) {
        navigate('/');
      } else {
        setErrorType('Erro ao criar senha. Tente novamente.');
      }
    } catch (err) {
      setErrorType('Erro inesperado. Tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6 w-full max-w-md mx-auto">
      <div className="text-center mb-6">
        <div className="flex justify-center mb-4">
          <img
            className="w-16 h-16 object-contain rounded-full"
            src={currentOrganization?.logo_url || '/logoipsum-custom-logogram.svg'}
            alt="logo"
          />
        </div>
        <h1 className="text-xl font-semibold text-gray-900 mb-2">Criar Nova Senha</h1>
        <p className="text-gray-600 text-sm">Complete seu cadastro criando uma senha</p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="email">
            Email
          </label>
          <input
            id="email"
            name="email"
            value={email || ''}
            readOnly
            className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-500 focus:outline-none"
            type="email"
            autoComplete="username"
          />
        </div>

        <FormInput
          name="name"
          control={control}
          id="name"
          label="Nome Completo"
          type="text"
          leftIcon={faUser}
          placeholder="Digite seu nome completo"
          autoComplete="name"
        />

        <FormInput
          name="password"
          control={control}
          id="password"
          label="Nova Senha"
          type="password"
          leftIcon={faLock}
          placeholder="Digite sua nova senha"
          autoComplete="new-password"
        />

        <FormInput
          name="passwordConfirm"
          control={control}
          id="passwordConfirm"
          label="Confirme a Senha"
          type="password"
          leftIcon={faLock}
          placeholder="Confirme sua nova senha"
          autoComplete="new-password"
        />

        {error && (
          <Alert
            variant="error"
            title="Erro"
            message={typeof error === 'string' ? error : 'Erro ao criar senha'}
          />
        )}

        <Button
          type="submit"
          variant="primary"
          size="lg"
          fullWidth
          loading={isLoading}
          disabled={isLoading || !isValid}
          leftIcon={!isLoading ? faEye : undefined}
        >
          {isLoading ? 'Criando...' : 'Criar Senha'}
        </Button>
      </form>
    </div>
  );
};

export default PasswordPage;
